import type {
  BarcodeTemplate,
  BoxElementTemplate,
  CompiledBarcodeElement,
  CompiledBoxElement,
  CompiledContainer,
  CompiledElement,
  CompiledLineElement,
  CompiledSpacerElement,
  CompiledTextElement,
  FontTemplate,
  LabelElementTemplate,
  LabelTemplateV2,
  LineElementTemplate,
  SpacerElementTemplate,
  TextElementTemplate,
} from "../types/template";
import { cloneWidthPolicy } from "../utils/barcode";
import { normalizeSpacing } from "../utils/spacing";
import { DEFAULT_CODE128, DEFAULT_QRCODE } from "./defaults";

function compileTextElement(
  element: TextElementTemplate,
  fontDefaults: FontTemplate,
): CompiledTextElement {
  return {
    type: "text",
    id: element.id,
    source: element.source,
    font: {
      ...fontDefaults,
      ...(element.font ?? {}),
    },
    fitMode: element.fitMode ?? "word-wrap",
    transform: element.transform ?? "none",
    reversePrint: element.reversePrint ?? false,
    presence: element.presence ?? "required",
    valueTemplate: element.valueTemplate ?? "{{value}}",
    marginMm: normalizeSpacing(element.marginMm),
    nudgeXMm: element.nudgeXMm ?? 0,
    nudgeYMm: element.nudgeYMm ?? 0,
    widthMm: element.widthMm,
    heightMm: element.heightMm,
  };
}

function compileBarcodeElement(
  element: Extract<LabelElementTemplate, { type: "barcode" }>,
  barcodeDefaults: BarcodeTemplate,
): CompiledBarcodeElement {
  const symbology = element.symbology ?? barcodeDefaults.symbology;
  const base = {
    type: "barcode" as const,
    id: element.id,
    source: element.source,
    marginMm: normalizeSpacing(element.marginMm),
    nudgeXMm: element.nudgeXMm ?? 0,
    nudgeYMm: element.nudgeYMm ?? 0,
    widthMm: element.widthMm,
  };

  if (symbology === "qrcode") {
    const qrDefaults =
      barcodeDefaults.symbology === "qrcode" ? barcodeDefaults : DEFAULT_QRCODE;
    return {
      ...base,
      symbology: "qrcode",
      magnification: element.magnification ?? qrDefaults.magnification,
      errorCorrection: element.errorCorrection ?? qrDefaults.errorCorrection,
      hAlign: element.hAlign ?? qrDefaults.hAlign,
      heightMm: element.heightMm,
    };
  }

  const c128Defaults =
    barcodeDefaults.symbology === "code128" ? barcodeDefaults : DEFAULT_CODE128;
  return {
    ...base,
    symbology: "code128",
    widthPolicy: cloneWidthPolicy(
      element.widthPolicy ?? c128Defaults.widthPolicy,
    ),
    hAlign: element.hAlign ?? c128Defaults.hAlign,
    wideToNarrowRatio:
      element.wideToNarrowRatio ?? c128Defaults.wideToNarrowRatio,
    printInterpretationLine:
      element.printInterpretationLine ?? c128Defaults.printInterpretationLine,
    interpretationAboveCode:
      element.interpretationAboveCode ?? c128Defaults.interpretationAboveCode,
    checkDigit: element.checkDigit ?? c128Defaults.checkDigit,
    optimizeNumeric: element.optimizeNumeric ?? c128Defaults.optimizeNumeric,
    heightMm: element.heightMm ?? c128Defaults.heightMm,
  };
}

function compileLineElement(element: LineElementTemplate): CompiledLineElement {
  return {
    type: "line",
    id: element.id,
    orientation: element.orientation ?? "horizontal",
    thicknessMm: element.thicknessMm ?? 0.3,
    marginMm: normalizeSpacing(element.marginMm),
    nudgeXMm: element.nudgeXMm ?? 0,
    nudgeYMm: element.nudgeYMm ?? 0,
    widthMm: element.widthMm,
    heightMm: element.heightMm,
  };
}

function compileBoxElement(element: BoxElementTemplate): CompiledBoxElement {
  return {
    type: "box",
    id: element.id,
    borderThicknessMm: element.borderThicknessMm ?? 0.3,
    fill: element.fill ?? false,
    cornerRounding: element.cornerRounding ?? 0,
    marginMm: normalizeSpacing(element.marginMm),
    nudgeXMm: element.nudgeXMm ?? 0,
    nudgeYMm: element.nudgeYMm ?? 0,
    widthMm: element.widthMm,
    heightMm: element.heightMm,
  };
}

function compileSpacerElement(
  element: SpacerElementTemplate,
): CompiledSpacerElement {
  return {
    type: "spacer",
    id: element.id,
    marginMm: normalizeSpacing(element.marginMm),
    nudgeXMm: element.nudgeXMm ?? 0,
    nudgeYMm: element.nudgeYMm ?? 0,
    widthMm: element.widthMm,
    heightMm: element.heightMm,
  };
}

function compileElement(
  element: LabelElementTemplate,
  fontDefaults: FontTemplate,
  barcodeDefaults: BarcodeTemplate,
): CompiledElement {
  switch (element.type) {
    case "text":
      return compileTextElement(element, fontDefaults);
    case "barcode":
      return compileBarcodeElement(element, barcodeDefaults);
    case "line":
      return compileLineElement(element);
    case "box":
      return compileBoxElement(element);
    case "spacer":
      return compileSpacerElement(element);
    default: {
      const exhaustiveCheck: never = element;
      throw new Error(
        `Unsupported element in compile step: ${String(exhaustiveCheck)}`,
      );
    }
  }
}

export function compileContainers(
  template: LabelTemplateV2,
  fontDefaults: FontTemplate,
  barcodeDefaults: BarcodeTemplate,
): CompiledContainer[] {
  return template.containers.map((container) => ({
    id: container.id,
    anchor: container.anchor,
    flow: container.flow,
    xMm: container.xMm ?? 0,
    yMm: container.yMm ?? 0,
    widthMm: container.widthMm,
    heightMm: container.heightMm,
    paddingMm: normalizeSpacing(container.paddingMm),
    gapMm: container.gapMm ?? 1,
    align: container.align ?? "start",
    justify: container.justify ?? "start",
    overflow: container.overflow ?? "error",
    elements: container.elements.map((element) =>
      compileElement(element, fontDefaults, barcodeDefaults),
    ),
  }));
}
